import { useState } from "react";

import { useAlertContext } from "@shared";

import { CaseNoteResponse } from "@/api";
import { printBlob } from "@/common/helper/printHelper";
import { fetchCaseNotePrintPdfBlob } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-action-buttons/hooks/caseNotePrintFetch.utils";
import { useCaseNoteDocumentBlob } from "@/pages/clients/client/components/tabs/notes-tab/components/case-notes-overview/hooks/useCaseNoteDocumentBlob";
import { useTranslation } from "@/translations";

/**
 * Prints the currently opened CaseNote (Vermerk).
 *
 * A document-based CaseNote (Vermerk) is printed from the document's PDF preview — the same blob the detail view
 * already renders, so React Query serves it from cache and the file is not fetched twice. For a PDF
 * upload that preview is the original file, so it reaches the printer unconverted.
 *
 * A text-based CaseNote (Vermerk) has no document, so the backend renders it onto the FSW letterhead on demand.
 * That is fetched on click rather than up front, so selecting notes does not generate PDFs nobody prints.
 */
export function useCaseNotePrint(clientId: string, caseNote: CaseNoteResponse | null) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const [isPrinting, setIsPrinting] = useState(false);

  const attachedDocument = caseNote?.attachedDocument ?? null;
  const { blob: documentBlob, errorKind } = useCaseNoteDocumentBlob(clientId, attachedDocument?.documentId);

  async function print() {
    if (!caseNote || isPrinting) {
      return;
    }

    setIsPrinting(true);
    try {
      if (attachedDocument) {
        if (errorKind === "notReady") {
          showError(t("clients.client.caseNotes.previewNotReady"));
          return;
        }
        if (errorKind || !documentBlob) {
          showError(t("clients.client.caseNotes.previewFailed"));
          return;
        }
        await printBlob(documentBlob);
        return;
      }

      const pdf = await fetchCaseNotePrintPdfBlob(clientId, caseNote.caseNoteId);
      await printBlob(pdf);
    } catch {
      showError(t("clients.client.caseNotes.printFailed"));
    } finally {
      setIsPrinting(false);
    }
  }

  return { print, isPrinting };
}
